import { Button, useMutationWithToast } from '@pesto/ui';
import { Label } from '@pesto/ui/components/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pesto/ui/components/Select';
import { useState } from 'react';

import type { ModalProps } from '../../types/modal.types';

import { type User, useListProgramsQuery, useUpdateAdminScopeMutation } from '../../../__generated__/graphql/api';
import { getUsersListMutationRefreshOptions } from '../../../utils/userCacheInvalidation';

const ALL_PROGRAMS = 'all-programs';

type EditAdminScopePayload = {
  userId: User['id'];
  partnerId?: string;
  partnerName?: string;
  adminProgramId?: User['adminProgramId'];
};

export const EditAdminScopeModalComponent = ({ payload, api }: ModalProps<EditAdminScopePayload, true>) => {
  const { partnerId, partnerName } = payload;
  const userId = payload.userId ?? '';
  const currentChoice = payload.adminProgramId ?? ALL_PROGRAMS;
  const [programChoice, setProgramChoice] = useState(currentChoice);

  const { handleMutation } = useMutationWithToast();
  const [updateAdminScope, { loading }] = useUpdateAdminScopeMutation(getUsersListMutationRefreshOptions());

  const { data: programsData } = useListProgramsQuery({
    variables: { fullResults: true, paginationDetailsOnly: false },
  });

  const partnerPrograms = (programsData?.listPrograms?.programs ?? []).filter(
    (program) => program.partner.id === partnerId && (program.active || program.id === currentChoice),
  );

  const handleSave = () => {
    const input = programChoice === ALL_PROGRAMS ? { userId, partnerId } : { userId, adminProgramId: programChoice };

    void handleMutation({
      mutation: () => updateAdminScope({ variables: { input } }),
      extractSuccess: (result) => !!result?.data?.updateAdminScope?.id,
      onSuccess: () => api.close(true),
      debugLabel: 'edit-admin-scope',
      toast: {
        success: 'Admin scope updated',
        error: 'Failed to update admin scope',
        loading: 'Updating admin scope...',
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        Partner: <span className="text-foreground font-medium">{partnerName}</span>
      </p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="admin-scope-program">Program</Label>
        <Select value={programChoice} onValueChange={setProgramChoice}>
          <SelectTrigger id="admin-scope-program">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PROGRAMS}>All programs</SelectItem>
            {partnerPrograms.map((program) => (
              <SelectItem key={program.id} value={program.id}>
                {program.active ? program.name : `${program.name} (inactive)`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => api.cancel()}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          loading={loading}
          disabled={programChoice === currentChoice}
        >
          Save scope
        </Button>
      </div>
    </div>
  );
};
